import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FileText, BookOpen, Download, ExternalLink, Users, TrendingUp, Shield, Zap, GitBranch, Layers, X, Maximize2, Activity } from 'lucide-react';

// Markdown Viewer Component
const MarkdownViewer = ({ markdownUrl, title, onClose }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(markdownUrl)
      .then(response => {
        if (!response.ok) throw new Error('Failed to load document');
        return response.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [markdownUrl]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen py-8 px-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="max-w-5xl mx-auto bg-black border-2 border-white shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-black border-b-2 border-white px-8 py-6 flex items-center justify-between z-10">
            <div className="flex items-center gap-4">
              <FileText size={32} className="text-blue-400" />
              <div>
                <h2 className="text-2xl font-bold text-white">{title}</h2>
                <p className="text-gray-400 text-sm mt-1">Interactive Document Viewer</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 transition-colors border border-white"
            >
              <X size={24} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-8">
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto"></div>
                <p className="text-gray-400 mt-4">Loading document...</p>
              </div>
            )}

            {error && (
              <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 text-center">
                <p className="text-red-400">Error: {error}</p>
              </div>
            )}

            {!loading && !error && (
              <div className="prose prose-invert prose-lg max-w-none
                prose-headings:text-blue-400 
                prose-h1:text-4xl prose-h1:mb-6 prose-h1:border-b prose-h1:border-gray-700 prose-h1:pb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-purple-400
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-blue-300
                prose-p:text-gray-300 prose-p:leading-relaxed
                prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-white prose-strong:font-semibold
                prose-code:text-green-400 prose-code:bg-gray-800 prose-code:px-2 prose-code:py-1 prose-code:rounded
                prose-pre:bg-gray-800 prose-pre:border prose-pre:border-gray-700
                prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-gray-800/50 prose-blockquote:italic
                prose-ul:text-gray-300 prose-ol:text-gray-300
                prose-li:my-2
                prose-table:border-collapse prose-table:w-full
                prose-th:bg-gray-800 prose-th:border prose-th:border-gray-700 prose-th:px-4 prose-th:py-2
                prose-td:border prose-td:border-gray-700 prose-td:px-4 prose-td:py-2
                prose-img:rounded-lg prose-img:shadow-lg"
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Whitepaper = () => {
  const [viewingDoc, setViewingDoc] = useState(null);

  const technicalSections = [
    {
      number: '1',
      title: 'Abstract',
      description: 'Overview of AVO hybrid consensus protocol designed for high-performance DeFi applications.',
      icon: <BookOpen size={24} />
    },
    {
      number: '2',
      title: 'Introduction & Blockchain Trilemma',
      description: 'How AVO addresses scalability, security, and decentralization simultaneously.',
      icon: <FileText size={24} />
    },
    {
      number: '3',
      title: 'Core Architecture',
      description: 'System components, data flow, and hierarchical design of FlowConsensus.',
      icon: <Layers size={24} />
    },
    {
      number: '4',
      title: 'Intra-Shard Consensus: BlockDAG + GHOSTDAG',
      description: 'Directed Acyclic Graph structure with GHOSTDAG algorithm for parallel processing.',
      icon: <GitBranch size={24} />
    },
    {
      number: '5',
      title: 'Inter-Shard Consensus: FlowConsensus',
      description: 'Global synchronization model with epoch-based coordination and cross-shard transactions.',
      icon: <Layers size={24} />
    },
    {
      number: '6',
      title: 'Cryptographic Primitives',
      description: 'BLS Signatures, Threshold Encryption, VRF, and Zero-Knowledge Proofs implementation.',
      icon: <Shield size={24} />
    },
    {
      number: '7',
      title: 'Economic Model',
      description: 'Fee market (EIP-1559), staking mechanics, validator rewards, and slashing conditions.',
      icon: <TrendingUp size={24} />
    },
    {
      number: '8',
      title: 'Network Layer & P2P',
      description: 'libp2p stack, Gossipsub, peer scoring, and network partition detection.',
      icon: <GitBranch size={24} />
    },
    {
      number: '9',
      title: 'Performance Analysis',
      description: 'Theoretical throughput, latency breakdown, computational complexity tables.',
      icon: <Zap size={24} />
    },
    {
      number: '10',
      title: 'Security Analysis',
      description: 'Byzantine Fault Tolerance, liveness guarantees, attack vectors and mitigations.',
      icon: <Shield size={24} />
    },
    {
      number: '11',
      title: 'Formal Verification',
      description: 'Safety invariants, liveness properties, and mathematical proofs.',
      icon: <FileText size={24} />
    },
    {
      number: '12',
      title: 'Implementation & Roadmap',
      description: 'Rust architecture, data structures, concurrency model, and development phases.',
      icon: <Layers size={24} />
    }
  ];

  const executiveSections = [
    {
      title: 'What is AVO?',
      description: 'Simple explanation of AVO Protocol and how it solves blockchain problems.',
      icon: <BookOpen size={24} />
    },
    {
      title: 'How AVO Works',
      description: 'Parallel processing, BlockDAG, MEV protection, and instant finality explained simply.',
      icon: <Zap size={24} />
    },
    {
      title: 'Key Technologies',
      description: 'FlowConsensus, cryptographic innovation, and EVM compatibility in plain English.',
      icon: <Layers size={24} />
    },
    {
      title: 'Real-World Performance',
      description: 'Speed and cost comparisons with Bitcoin, Ethereum, Solana, and others.',
      icon: <Activity size={24} />
    },
    {
      title: 'Use Cases',
      description: 'DEXs, high-frequency trading, GameFi, payments, and asset tokenization.',
      icon: <Users size={24} />
    },
    {
      title: 'Token Economics',
      description: '1B AVO distribution, staking rewards, governance, and deflationary mechanism.',
      icon: <TrendingUp size={24} />
    },
    {
      title: 'Security & Decentralization',
      description: 'Byzantine fault tolerance, validator requirements, and planned audits.',
      icon: <Shield size={24} />
    },
    {
      title: 'Getting Started',
      description: 'How to develop, validate, or use AVO - practical guides for everyone.',
      icon: <GitBranch size={24} />
    },
    {
      title: 'Competitive Advantages',
      description: 'Why AVO wins: 500k+ TPS, MEV protection, low fees, and full EVM compatibility.',
      icon: <Zap size={24} />
    },
    {
      title: 'FAQ & Community',
      description: 'Common questions and how to join the AVO ecosystem.',
      icon: <FileText size={24} />
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-black text-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 text-white">
            AVO Protocol Documentation
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
            Comprehensive technical and executive documentation for the AVO blockchain protocol
          </p>
          <div className="flex justify-center gap-4 text-sm text-gray-400 mb-4">
            <span>Version 1.0</span>
            <span>•</span>
            <span>October 2025</span>
            <span>•</span>
            <span>AVO Research Team</span>
          </div>
          
          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl mx-auto bg-black border-2 border-white p-4 text-center"
          >
            <p className="text-sm text-gray-300">
              ✨ <strong className="text-white">Interactive Viewer:</strong> Click "View Document" for a beautiful formatted experience with syntax highlighting, 
              or "Download Markdown" to save locally.
            </p>
          </motion.div>
        </motion.div>

        {/* Two Main Documents */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {/* Technical Whitepaper */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-black border-2 border-white p-8 hover:border-blue-400 transition-all group"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-black border-2 border-white group-hover:border-blue-400 transition-all">
                <FileText size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">Technical Whitepaper</h2>
                <p className="text-gray-400">For Developers & Researchers</p>
              </div>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Deep-dive into AVO's architecture with mathematical formulas, cryptographic specifications, 
              performance analysis, and formal verification. Includes complexity tables, protocol flows, 
              and implementation details in Rust.
            </p>

            <div className="bg-gray-950 border border-white p-4 mb-6">
              <h3 className="text-lg font-bold text-white mb-3">What's Inside:</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">▸</span>
                  <span><strong>1,200+ lines</strong> of technical analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">▸</span>
                  <span><strong>Mathematical formulas</strong> for consensus validation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">▸</span>
                  <span><strong>Algorithm pseudocode</strong> with Big-O complexity</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">▸</span>
                  <span><strong>Cryptographic primitives</strong> (BLS, Threshold, zk-SNARKs)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">▸</span>
                  <span><strong>Security proofs</strong> and attack vector analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">▸</span>
                  <span><strong>Rust code snippets</strong> from actual implementation</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => setViewingDoc({ 
                  url: `${process.env.PUBLIC_URL}/WHITEPAPER_TECHNICAL.md`,
                  title: 'Technical Whitepaper'
                })}
                className="flex items-center justify-center gap-2 bg-white hover:bg-blue-400 text-black hover:text-white font-bold py-3 px-6 border-2 border-white transition-all"
              >
                <Maximize2 size={20} />
                View Document
              </button>
              <a
                href={`${process.env.PUBLIC_URL}/WHITEPAPER_TECHNICAL.md`}
                download="AVO_Technical_Whitepaper.md"
                className="flex items-center justify-center gap-2 bg-black hover:bg-blue-400 text-white font-semibold py-3 px-6 border-2 border-white transition-all"
              >
                <Download size={20} />
                Download Markdown
              </a>
              <a
                href="https://github.com/avo-protocol/whitepaper/blob/main/TECHNICAL.md"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-black hover:bg-blue-400 text-white font-semibold py-3 px-6 border-2 border-white transition-all"
              >
                <ExternalLink size={20} />
                View on GitHub
              </a>
            </div>
          </motion.div>

          {/* Executive Whitepaper (Litepaper) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-black border-2 border-white p-8 hover:border-green-400 transition-all group"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-black border-2 border-white group-hover:border-green-400 transition-all">
                <BookOpen size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">Litepaper (Executive Summary)</h2>
                <p className="text-gray-400">For Everyone - Simple & Accessible</p>
              </div>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Easy-to-understand guide explaining AVO Protocol in plain English. Covers how it works, 
              key advantages, use cases, and tokenomics without heavy technical jargon.
            </p>

            <div className="bg-gray-950 border border-white p-4 mb-6">
              <h3 className="text-lg font-bold text-white mb-3">What's Inside:</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">▸</span>
                  <span><strong>Simple explanations</strong> - No complex math or code</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">▸</span>
                  <span><strong>How AVO works</strong> - Sharding, BlockDAG, MEV protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">▸</span>
                  <span><strong>Performance comparison</strong> vs Bitcoin, Ethereum, Solana</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">▸</span>
                  <span><strong>Real use cases</strong> - DEXs, gaming, payments, tokenization</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">▸</span>
                  <span><strong>Token economics</strong> - 1B AVO distribution & rewards</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">▸</span>
                  <span><strong>Getting started</strong> - For developers, validators, users</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => setViewingDoc({ 
                  url: `${process.env.PUBLIC_URL}/WHITEPAPER.md`,
                  title: 'AVO Litepaper - Simple Guide'
                })}
                className="flex items-center justify-center gap-2 bg-white hover:bg-green-400 text-black hover:text-white font-bold py-3 px-6 border-2 border-white transition-all"
              >
                <Maximize2 size={20} />
                View Document
              </button>
              <a
                href={`${process.env.PUBLIC_URL}/WHITEPAPER.md`}
                download="AVO_Litepaper.md"
                className="flex items-center justify-center gap-2 bg-black hover:bg-green-400 text-white font-semibold py-3 px-6 border-2 border-white transition-all"
              >
                <Download size={20} />
                Download Markdown
              </a>
              <a
                href="https://github.com/avo-protocol/whitepaper/blob/main/EXECUTIVE.md"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-black hover:bg-green-400 text-white font-semibold py-3 px-6 border-2 border-white transition-all"
              >
                <ExternalLink size={20} />
                View on GitHub
              </a>
            </div>
          </motion.div>
        </div>

        {/* Technical Whitepaper Sections */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <div className="flex items-center gap-4 mb-8">
            <FileText size={40} className="text-white" />
            <h2 className="text-4xl font-bold text-white">Technical Whitepaper Contents</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {technicalSections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="bg-black border-2 border-white p-4 hover:border-blue-400 transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-shrink-0 w-10 h-10 bg-black border-2 border-white group-hover:border-blue-400 text-white transition-all flex items-center justify-center font-bold text-lg">
                    {section.number}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-white mb-1 leading-tight">{section.title}</h4>
                  </div>
                </div>
                <p className="text-xs text-gray-400">{section.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 bg-black border-2 border-white p-6">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="text-blue-400" size={28} />
              Key Technical Highlights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-3xl font-bold text-blue-400 mb-1">520,000+</p>
                <p className="text-sm text-gray-300">TPS Theoretical Capacity</p>
                <p className="text-xs text-gray-500">With 64 shards & 1,000 validators</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-3xl font-bold text-green-400 mb-1">&lt;1 sec</p>
                <p className="text-sm text-gray-300">Intra-Shard Finality</p>
                <p className="text-xs text-gray-500">BLS aggregation & DAG consensus</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-3xl font-bold text-purple-400 mb-1">2-5 sec</p>
                <p className="text-sm text-gray-300">Cross-Shard Finality</p>
                <p className="text-xs text-gray-500">FlowConsensus coordination</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-3xl font-bold text-yellow-400 mb-1">$0.001</p>
                <p className="text-sm text-gray-300">Average TX Fee</p>
                <p className="text-xs text-gray-500">EIP-1559 dynamic fee market</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-3xl font-bold text-red-400 mb-1">O(1)</p>
                <p className="text-sm text-gray-300">BLS Verification</p>
                <p className="text-xs text-gray-500">Aggregated signature checking</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-3xl font-bold text-indigo-400 mb-1">100%</p>
                <p className="text-sm text-gray-300">EVM Compatible</p>
                <p className="text-xs text-gray-500">REVM (Rust implementation)</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Litepaper Sections */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-20"
        >
          <div className="flex items-center gap-4 mb-8">
            <BookOpen size={40} className="text-white" />
            <h2 className="text-4xl font-bold text-white">Litepaper Contents</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {executiveSections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="bg-black border-2 border-white p-4 hover:border-green-400 transition-all cursor-pointer group"
              >
                <div className="flex flex-col items-center text-center gap-3 mb-2">
                  <div className="p-3 bg-black border-2 border-white group-hover:border-green-400 transition-all">
                    {React.cloneElement(section.icon, { className: 'text-white' })}
                  </div>
                  <h4 className="text-sm font-bold text-white leading-tight">{section.title}</h4>
                </div>
                <p className="text-xs text-gray-400 text-center">{section.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 bg-black border-2 border-white p-6">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="text-green-400" size={28} />
              Key Performance Metrics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-2xl font-bold text-green-400 mb-1">520k TPS</p>
                <p className="text-sm text-gray-300">Transaction Speed</p>
                <p className="text-xs text-gray-500">64 shards × 10k TPS each</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-2xl font-bold text-blue-400 mb-1">$0.001</p>
                <p className="text-sm text-gray-300">Average Fee</p>
                <p className="text-xs text-gray-500">100-1000x cheaper than ETH</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-2xl font-bold text-purple-400 mb-1">&lt;1 sec</p>
                <p className="text-sm text-gray-300">Finality Time</p>
                <p className="text-xs text-gray-500">Instant confirmation</p>
              </div>
              <div className="bg-gray-950 border border-white p-4">
                <p className="text-2xl font-bold text-yellow-400 mb-1">1000+</p>
                <p className="text-sm text-gray-300">Validators Target</p>
                <p className="text-xs text-gray-500">Fully decentralized</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Additional Resources */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-4xl font-bold text-white mb-8 text-center">Additional Resources</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black border-2 border-white p-6 hover:border-cyan-400 transition-all group">
              <GitBranch className="text-white group-hover:text-cyan-400 mb-4 transition-colors" size={32} />
              <h3 className="text-xl font-bold text-white mb-2">GitHub Repository</h3>
              <p className="text-gray-400 text-sm mb-4">
                Explore the open-source codebase, contribute to development, and review implementation details.
              </p>
              <a
                href="https://github.com/avo-protocol"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold flex items-center gap-1"
              >
                View on GitHub <ExternalLink size={14} />
              </a>
            </div>

            <div className="bg-black border-2 border-white p-6 hover:border-purple-400 transition-all group">
              <FileText className="text-white group-hover:text-purple-400 mb-4 transition-colors" size={32} />
              <h3 className="text-xl font-bold text-white mb-2">Technical Documentation</h3>
              <p className="text-gray-400 text-sm mb-4">
                Developer guides, API references, smart contract examples, and integration tutorials.
              </p>
              <a
                href="https://docs.avoprotocol.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 text-sm font-semibold flex items-center gap-1"
              >
                Read Docs <ExternalLink size={14} />
              </a>
            </div>

            <div className="bg-black border-2 border-white p-6 hover:border-pink-400 transition-all group">
              <Users className="text-white group-hover:text-pink-400 mb-4 transition-colors" size={32} />
              <h3 className="text-xl font-bold text-white mb-2">Community & Support</h3>
              <p className="text-gray-400 text-sm mb-4">
                Join Discord, Telegram, and Twitter to connect with developers, validators, and investors.
              </p>
              <a
                href="https://discord.gg/avoprotocol"
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-400 hover:text-pink-300 text-sm font-semibold flex items-center gap-1"
              >
                Join Community <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-black border-2 border-white p-12 text-center"
        >
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Build on AVO?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Whether you're a developer, investor, or validator - there's a place for you in the AVO ecosystem.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white hover:bg-blue-400 text-black hover:text-white font-bold py-3 px-8 border-2 border-white transition-all">
              Developer Portal
            </button>
            <button className="bg-white hover:bg-green-400 text-black hover:text-white font-bold py-3 px-8 border-2 border-white transition-all">
              Join Token Sale
            </button>
            <button className="bg-white hover:bg-purple-400 text-black hover:text-white font-bold py-3 px-8 border-2 border-white transition-all">
              Run a Validator
            </button>
          </div>
        </motion.div>
      </div>

      {/* Markdown Viewer Modal */}
      <AnimatePresence>
        {viewingDoc && (
          <MarkdownViewer
            markdownUrl={viewingDoc.url}
            title={viewingDoc.title}
            onClose={() => setViewingDoc(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Whitepaper;
